import { apiService } from '../../services/api';
import { StreamHandlers, streamSse } from '../intelligence/intelligenceApi';
import { toParams } from '../monitoring/monitoringApi';
import type {
  CreateIssueInput,
  IssueMap,
  IssueQuery,
  IssueView,
  IssuesOverview,
  MovementEntry,
  RecordStanceInput,
  SaveScenarioInput,
  ScenarioProjection,
  ScenarioView,
  StanceHistory,
  StanceView,
  SubjectType,
  UpdateIssueInput,
} from '../../types/issues';

const BASE = '/api/issues';

/** The backend on this server predates `/api/issues` — settings cannot fix that. */
export class IssuesUnavailableError extends Error {
  constructor(message = 'Issues are not available on this server yet.') {
    super(message);
    this.name = 'IssuesUnavailableError';
  }
}

/** A 404 on a collection means the module is not deployed, not "nothing found" */
function asUnavailable(error: any): never {
  if (error?.response?.status === 404) throw new IssuesUnavailableError();
  throw error;
}

/** One short line a user can act on, never a raw stack or a bare status code */
export function issuesErrorMessage(error: any): string {
  if (error instanceof IssuesUnavailableError) return error.message;
  const data = error?.response?.data;
  const body = data?.message ?? data?.error?.message;
  const raw = Array.isArray(body) ? body.join(', ') : body || error?.message || '';
  const text = String(raw).trim();
  const status = error?.response?.status;
  if (status === 401) return 'Your session has expired — sign in again';
  if (status === 403) return 'You do not have permission to do that';
  if (/network error|failed to fetch|load failed/i.test(text)) return 'Cannot reach the server — check your connection';
  if (status >= 500) return text && text.length <= 140 ? `Server error: ${text}` : 'The server could not complete that — try again';
  return text || 'Something went wrong';
}

/* ------------------------------------------------------------------ */
/* Issues                                                              */
/* ------------------------------------------------------------------ */

export async function listIssues(query: IssueQuery = {}): Promise<IssueView[]> {
  try {
    const res = await apiService.get<IssueView[]>(BASE, { params: toParams(query) });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function issuesOverview(): Promise<IssuesOverview> {
  try {
    const res = await apiService.get<IssuesOverview>(`${BASE}/overview`);
    return res.data;
  } catch (error) {
    return asUnavailable(error);
  }
}

export async function getIssue(idOrSlug: string): Promise<IssueView> {
  const res = await apiService.get<IssueView>(`${BASE}/${idOrSlug}`);
  return res.data;
}

export async function createIssue(input: CreateIssueInput): Promise<IssueView> {
  const res = await apiService.post<IssueView>(BASE, input);
  return res.data;
}

export async function updateIssue(id: string, input: UpdateIssueInput): Promise<IssueView> {
  const res = await apiService.patch<IssueView>(`${BASE}/${id}`, input);
  return res.data;
}

export async function deleteIssue(id: string): Promise<void> {
  await apiService.delete(`${BASE}/${id}`);
}

/* ------------------------------------------------------------------ */
/* Stances                                                             */
/* ------------------------------------------------------------------ */

export async function listStances(issueId: string): Promise<StanceView[]> {
  const res = await apiService.get<StanceView[]>(`${BASE}/${issueId}/stances`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function recordStance(issueId: string, input: RecordStanceInput): Promise<StanceView> {
  const res = await apiService.post<StanceView>(`${BASE}/${issueId}/stances`, input);
  return res.data;
}

export async function stanceHistory(
  issueId: string,
  subjectType: SubjectType,
  subjectId: string,
): Promise<StanceHistory> {
  const res = await apiService.get<StanceHistory>(
    `${BASE}/${issueId}/stances/${subjectType}/${subjectId}`,
  );
  return res.data;
}

export async function issueMovements(issueId: string, since?: string): Promise<MovementEntry[]> {
  const res = await apiService.get<MovementEntry[]>(`${BASE}/${issueId}/movements`, {
    params: toParams({ since }),
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** Movements across every issue, for the dashboard */
export async function allMovements(since?: string): Promise<MovementEntry[]> {
  try {
    const res = await apiService.get<MovementEntry[]>(`${BASE}/movements`, {
      params: toParams({ since }),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

/** Every issue one entity has a position on — for their profile */
export async function issuesForSubject(
  subjectType: SubjectType,
  subjectId: string,
): Promise<{ issue: IssueView; stance: StanceView }[]> {
  try {
    const res = await apiService.get<{ issue: IssueView; stance: StanceView }[]>(
      `${BASE}/by-subject/${subjectType}/${subjectId}`,
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return asUnavailable(error);
  }
}

/* ------------------------------------------------------------------ */
/* Map                                                                 */
/* ------------------------------------------------------------------ */

export async function getIssueMap(issueId: string): Promise<IssueMap> {
  const res = await apiService.get<IssueMap>(`${BASE}/${issueId}/map`);
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Scenarios                                                           */
/* ------------------------------------------------------------------ */

export async function listScenarios(issueId: string): Promise<ScenarioView[]> {
  const res = await apiService.get<ScenarioView[]>(`${BASE}/${issueId}/scenarios`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function createScenario(issueId: string, input: SaveScenarioInput): Promise<ScenarioView> {
  const res = await apiService.post<ScenarioView>(`${BASE}/${issueId}/scenarios`, input);
  return res.data;
}

export async function updateScenario(
  issueId: string,
  scenarioId: string,
  input: Partial<SaveScenarioInput>,
): Promise<ScenarioView> {
  const res = await apiService.patch<ScenarioView>(
    `${BASE}/${issueId}/scenarios/${scenarioId}`,
    input,
  );
  return res.data;
}

export async function deleteScenario(issueId: string, scenarioId: string): Promise<void> {
  await apiService.delete(`${BASE}/${issueId}/scenarios/${scenarioId}`);
}

export async function runScenario(issueId: string, scenarioId: string): Promise<ScenarioProjection> {
  const res = await apiService.post<ScenarioProjection>(
    `${BASE}/${issueId}/scenarios/${scenarioId}/run`,
    {},
  );
  return res.data;
}

/** Try a scenario without saving it, so the UI can show a projection live */
export async function previewScenario(
  issueId: string,
  input: SaveScenarioInput,
): Promise<ScenarioProjection> {
  const res = await apiService.post<ScenarioProjection>(
    `${BASE}/${issueId}/scenarios/preview`,
    input,
  );
  return res.data;
}

/* ------------------------------------------------------------------ */
/* AI                                                                  */
/* ------------------------------------------------------------------ */

/** Proposes stances for entities with none — an analyst accepts or ignores them */
export function assessIssue(issueId: string, handlers: StreamHandlers) {
  return streamSse(`${BASE}/${issueId}/assess`, {}, handlers);
}

/** Writes the assessment: balance, coalitions, who to move first */
export function briefIssue(issueId: string, handlers: StreamHandlers) {
  return streamSse(`${BASE}/${issueId}/brief`, {}, handlers);
}
